import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const sections = [
  {
    title: "1. Acceptance of Terms",
    content: `By accessing or using VicShield ("the Platform"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Platform. These Terms constitute a legally binding agreement between you and VicShield Global ("VicShield", "we", "us", or "our").
VicShield is an enterprise cyber risk intelligence platform providing risk quantification, regulatory compliance mapping, insurance intelligence, and related services to business subscribers. The Platform is intended solely for business use and is not available to individuals acting in a personal capacity.`
  },
  {
    title: "2. Eligibility and Account Registration",
    content: `To use VicShield, you must:
• Be a representative of a legitimate business organisation
• Provide a valid corporate email address (personal email providers are not accepted)
• Be at least 18 years of age
• Have the authority to bind your organisation to these Terms
You are responsible for maintaining the security of your account credentials. You must notify us immediately at support@vicshield.com if you suspect unauthorised access to your account. VicShield is not liable for any loss resulting from unauthorised use of your account.`
  },
  {
    title: "3. Subscription Plans and Payment",
    content: `VicShield offers three subscription tiers: Starter ($199/mo), Professional ($499/mo), and Enterprise ($999/mo). Annual billing is available at a 20% discount.
Subscriptions are billed in advance on a monthly or annual basis. All subscription payments are non-refundable. By providing payment information, you authorise VicShield to charge your payment method for the applicable subscription fees.
VicShield reserves the right to modify pricing with 30 days written notice to subscribers. Continued use of the Platform after a price change constitutes acceptance of the new pricing.`
  },
  {
    title: "4. Payment and No-Refund Policy",
    content: `Subscriptions are billed in advance on a monthly or annual basis. All subscription payments are non-refundable.
VicShield offers a 7-day free trial before any payment is required. During the free trial you have access to the dashboard and assessment features, giving you full opportunity to evaluate the Platform before committing to a paid subscription. By subscribing after your trial, you confirm you have had adequate opportunity to evaluate the Platform.
If you experience a technical issue preventing you from accessing the Platform, contact support@vicshield.com and we will investigate and resolve the issue promptly. Where a technical failure on our part prevents access for an extended period, we will provide appropriate remedy on a case-by-case basis.
By providing payment information, you authorise VicShield to charge your payment method for the applicable subscription fees on the agreed billing cycle.`
  },
  {
    title: "5. Acceptable Use",
    content: `You agree to use VicShield only for lawful purposes and in accordance with these Terms. You must not:
• Use the Platform to assess organisations you do not have authorisation to audit
• Attempt to reverse engineer, decompile, or extract the Platform's source code or algorithms
• Use automated tools to scrape, crawl, or extract data from the Platform
• Share your account credentials with individuals outside your organisation
• Use the Platform to generate fraudulent risk assessments or reports
• Attempt to circumvent entity limits or subscription restrictions
• Use the Platform in any way that violates applicable laws or regulations
VicShield reserves the right to suspend or terminate accounts that violate these provisions without refund.`
  },
  {
    title: "6. Accuracy of Assessment Data",
    content: `The accuracy of VicShield's risk intelligence is contingent upon the accuracy of the data you provide during assessments. VicShield's risk scores, financial exposure figures, and compliance alerts are generated based on your self-reported audit answers.
VicShield does not independently verify the accuracy of information you provide. You acknowledge that providing inaccurate, incomplete, or misleading information will result in inaccurate risk intelligence outputs. VicShield accepts no liability for decisions made based on assessments completed with inaccurate input data.`
  },
  {
    title: "7. Intellectual Property",
    content: `VicShield and its licensors retain all intellectual property rights in the Platform, including but not limited to the risk engine methodology, report templates, regulatory database, and all software code.
Your subscription grants you a limited, non-exclusive, non-transferable licence to access and use the Platform for your internal business purposes. You may not reproduce, distribute, or create derivative works from any Platform content without our express written consent.
Reports and documents generated by the Platform from your assessment data may be used internally within your organisation and shared with your board, insurers, and regulators.`
  },
  {
    title: "8. Disclaimer of Warranties",
    content: `VicShield is provided "as is" and "as available" without warranties of any kind, express or implied. We do not warrant that:
• The Platform will be uninterrupted, error-free, or completely secure
• Risk scores or financial exposure figures will be accurate for insurance underwriting purposes without independent verification
• Regulatory alerts will capture every applicable legal obligation in your jurisdiction
• Dark web scanning results will identify all credential exposures
VicShield's outputs are intelligence tools to support decision-making, not definitive legal, financial, or insurance advice. You should seek independent professional advice for material decisions.`
  },
  {
    title: "9. Limitation of Liability",
    content: `To the maximum extent permitted by applicable law, VicShield's total liability to you for any claims arising from your use of the Platform shall not exceed the total subscription fees paid by you in the 12 months preceding the claim.
VicShield is not liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunity, arising from your use of or inability to use the Platform.`
  },
  {
    title: "10. Data Protection and Privacy",
    content: `VicShield processes your personal and business data in accordance with our Privacy Policy, which is incorporated into these Terms by reference. By using the Platform, you consent to such processing.
VicShield acts as a data controller for account data and a data processor for assessment data you submit. We implement appropriate technical and organisational measures to protect your data in accordance with UK GDPR and applicable data protection legislation.
Your assessment data is never sold to third parties. It is used exclusively to generate your risk intelligence outputs.`
  },
  {
    title: "11. Termination",
    content: `Either party may terminate this agreement at any time. You may cancel your subscription through your account Settings page. Cancellation takes effect at the end of your current billing period.
VicShield may suspend or terminate your account immediately if you breach these Terms, fail to pay applicable fees, or if we are required to do so by law.
Upon termination, your data is retained for 14 days during which you may export your reports. After 14 days, all your data is permanently deleted from our systems.`
  },
  {
    title: "12. Governing Law",
    content: `These Terms are governed by and construed in accordance with the laws of the Republic of Lithuania. Any disputes arising from these Terms or your use of VicShield shall be subject to the jurisdiction of the competent courts of Vilnius, Lithuania, without prejudice to your rights as a consumer under the laws of your own country.
If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.`
  },
  {
    title: "13. External Domain Scanning and Passive Reconnaissance",
    content: `VicShield performs passive reconnaissance of publicly available domain infrastructure data using industry-standard threat intelligence sources. Scans may only be initiated by verified employees of the organisation being assessed. VicShield does not perform active penetration testing, intrusive scanning, or any form of unauthorised system access.

By using VicShield's public domain scanning feature, you confirm and warrant that:
• You are a verified employee of the organisation that owns or operates the domain being scanned
• You have completed corporate email verification by receiving and entering a one-time password (OTP) sent to your corporate email address at the domain being scanned
• You have the authority to request security intelligence gathering for that domain
• You consent to VicShield performing passive external reconnaissance on that domain using publicly available data sources including Shodan, HaveIBeenPwned, and DNS records

VicShield's domain scanning is entirely passive — we do not actively probe, penetrate, test, or interfere with any systems. We only read data that is already publicly indexed by third-party security intelligence services. All scan results are based solely on publicly available information and do not involve accessing, probing, or interacting with any private systems or networks.

You must not attempt to scan domain names belonging to organisations you do not represent or have authorisation to audit. Submitting unauthorised domains, or attempting to circumvent the corporate email verification process, is a violation of these Terms and may constitute a breach of applicable computer misuse laws in your jurisdiction.

VicShield accepts no liability for the accuracy or completeness of third-party security intelligence data returned for any domain.`
  },
  {
    title: "14. Changes to Terms",
    content: `VicShield reserves the right to modify these Terms at any time. We will provide 30 days written notice of material changes via email to your registered address. Continued use of the Platform after the effective date of changes constitutes acceptance of the revised Terms.
Last updated: June 2026`
  },
];

function Section({ title, content }: { title: string; content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
      >
        <span className="text-sm font-black text-slate-900 uppercase tracking-tight">
          {title}
        </span>
        <ChevronDown size={18} className={`text-slate-400 flex-shrink-0
          transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="text-slate-500 text-sm font-medium leading-relaxed pb-5 whitespace-pre-line">
              {content}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Terms() {
  const navigate = useNavigate();
  return (
    <div className="bg-white font-['Inter'] min-h-screen">
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <div onClick={() => navigate("/")} className="flex items-center gap-3 cursor-pointer group">
            <div className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-200 group-hover:scale-105 transition-transform">
              <span className="text-white font-black text-base">V</span>
            </div>
            <span className="text-xl font-black tracking-tighter text-slate-900 uppercase">VicShield</span>
          </div>
          <button onClick={() => navigate(-1)}
            className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-colors flex items-center gap-1">
            ← Back
          </button>
        </div>
      </nav>
      <div className="bg-[#0f172a] py-16">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-3">Legal</p>
          <h1 className="text-4xl font-black text-white tracking-tighter uppercase mb-4">Terms of Service</h1>
          <p className="text-slate-400 font-medium">Last updated: June 2026 · Effective immediately upon account creation</p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-[2.5rem] border-2 border-slate-100 px-10 py-2 shadow-sm mb-8">
          {sections.map((s, i) => (
            <Section key={i} title={s.title} content={s.content} />
          ))}
        </div>
        <div className="bg-[#0f172a] rounded-[2rem] p-8 text-center">
          <p className="text-slate-400 text-sm font-medium mb-2">Questions about these Terms?</p>
          <a href="mailto:support@vicshield.com" className="text-red-400 font-black text-sm hover:text-red-300 transition-colors">
            support@vicshield.com
          </a>
        </div>
      </div>
      <footer className="border-t border-slate-100 py-6 px-6">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">© 2026 VicShield Global</span>
          <div className="flex gap-6">
            <button onClick={() => navigate("/privacy")} className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-red-500 transition-colors">Privacy Policy</button>
            <button onClick={() => navigate("/pricing")} className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-red-500 transition-colors">Pricing</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
